import React, { useMemo } from 'react';
import { ApprovalRequest } from '../../../services/approvalService';
import ApprovalRequestCard from './ApprovalRequestCard';
import { Users, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';

function getLocationKey(request: ApprovalRequest): string {
  const loc = request.requestData?.location;
  if (!loc) return '__no_location__';
  if (typeof loc === 'string') return loc;
  const o = loc as { _id?: string; name?: string };
  if (o._id) return o._id;
  if (o.name) return String(o.name);
  return '__no_location__';
}

function getLocationDisplayName(request: ApprovalRequest): string {
  const loc = request.requestData?.location;
  if (!loc) return 'No location';
  if (typeof loc === 'string') return loc;
  const o = loc as { name?: string };
  return o.name || 'Unknown location';
}

interface ApprovalRequestsListProps {
  approvalRequests: ApprovalRequest[];
  loading?: boolean;
  onApprove?: (request: ApprovalRequest) => void;
  onReject?: (request: ApprovalRequest) => void;
  onView?: (request: ApprovalRequest) => void;
  onDelete?: (request: ApprovalRequest) => void;
  isApprover?: boolean;
  isRequester?: boolean;
  groupByUser?: boolean;
  groupByLocation?: boolean;
  page?: number;
  rowsPerPage?: number;
  totalCount?: number;
  onPageChange?: (newPage: number) => void;
  onRowsPerPageChange?: (newRowsPerPage: number) => void;
}

const ApprovalRequestsList: React.FC<ApprovalRequestsListProps> = ({
  approvalRequests,
  loading = false,
  onApprove,
  onReject,
  onView,
  onDelete,
  isApprover = false,
  isRequester = false,
  groupByUser = true,
  groupByLocation = false,
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const groupedRequests = useMemo(() => {
    if (groupByLocation) {
      const grouped: Record<string, ApprovalRequest[]> = {};
      approvalRequests.forEach((request) => {
        const key = getLocationKey(request);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(request);
      });
      return grouped;
    }
    if (!groupByUser) return { ungrouped: approvalRequests };
    const grouped: Record<string, ApprovalRequest[]> = {};
    approvalRequests.forEach((request) => {
      const key = request.requesterId._id;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(request);
    });
    return grouped;
  }, [approvalRequests, groupByUser, groupByLocation]);

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const startIndex = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const endIndex = Math.min((page + 1) * rowsPerPage, totalCount);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="flex flex-col items-center gap-3">
          <div className="w-9 h-9 border-2 border-gray-200 border-t-gray-700 rounded-full animate-spin" />
          <p className="text-xs font-medium text-gray-500">Loading requests…</p>
        </div>
      </div>
    );
  }

  if (approvalRequests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50">
        <Users className="w-10 h-10 text-gray-300 mb-2" />
        <p className="text-sm font-medium text-gray-700">No approval requests</p>
        <p className="text-xs text-gray-500 mt-0.5">There are no requests to show</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groupByLocation ? (
        Object.entries(groupedRequests).map(([locationKey, requests]) => {
          const locationName = getLocationDisplayName(requests[0]!);
          return (
            <div key={locationKey} className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{locationName}</p>
                    <p className="text-[11px] text-gray-500">Location</p>
                  </div>
                </div>
                <span className="text-[10px] font-medium text-gray-600 bg-gray-200 px-2 py-0.5 rounded-md">
                  {requests.length} {requests.length === 1 ? 'request' : 'requests'}
                </span>
              </div>
              <div className="p-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {requests.map((request) => (
                  <ApprovalRequestCard
                    key={request._id}
                    request={request}
                    onApprove={onApprove}
                    onReject={onReject}
                    onView={onView}
                    onDelete={onDelete}
                    isApprover={isApprover}
                    isRequester={isRequester}
                  />
                ))}
              </div>
            </div>
          );
        })
      ) : groupByUser ? (
        Object.entries(groupedRequests).map(([userId, requests]) => {
          const requesterName = requests[0]?.requesterName ?? 'Unknown';
          const requesterEmail = requests[0]?.requesterEmail ?? '';
          return (
            <div key={userId} className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{requesterName}</p>
                    {requesterEmail && (
                      <p className="text-[11px] text-gray-500 truncate">{requesterEmail}</p>
                    )}
                  </div>
                </div>
                <span className="text-[10px] font-medium text-gray-600 bg-gray-200 px-2 py-0.5 rounded-md">
                  {requests.length} {requests.length === 1 ? 'request' : 'requests'}
                </span>
              </div>
              <div className="p-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {requests.map((request) => (
                  <ApprovalRequestCard
                    key={request._id}
                    request={request}
                    onApprove={onApprove}
                    onReject={onReject}
                    onView={onView}
                    onDelete={onDelete}
                    isApprover={isApprover}
                    isRequester={isRequester}
                  />
                ))}
              </div>
            </div>
          );
        })
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {approvalRequests.map((request) => (
            <ApprovalRequestCard
              key={request._id}
              request={request}
              onApprove={onApprove}
              onReject={onReject}
              onView={onView}
              onDelete={onDelete}
              isApprover={isApprover}
              isRequester={isRequester}
            />
          ))}
        </div>
      )}

      {onPageChange && onRowsPerPageChange && totalCount > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-1 py-2 border-t border-gray-200">
          <div className="flex items-center gap-3 order-2 sm:order-1">
            <span className="text-xs text-gray-600">
              Showing <span className="font-medium">{startIndex}</span>–<span className="font-medium">{endIndex}</span> of{' '}
              <span className="font-medium">{totalCount}</span>
            </span>
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              Per page
              <select
                value={rowsPerPage}
                onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
                className="text-xs border border-gray-300 rounded-md py-1 px-2 focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
          </div>
          <nav className="flex items-center gap-1 order-1 sm:order-2" aria-label="Pagination">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(0, page - 1))}
              disabled={page === 0}
              className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-gray-600 px-2">
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default ApprovalRequestsList;
